import { HttpErrorResponse } from "@angular/common/http";
import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { StateService } from "@uirouter/core";
import { cloneDeep, isEqual, merge } from "lodash";
import { finalize } from "rxjs/operators";
import { DeployService } from "../../models/deploy.service";
import { NoticeService } from "../../models/notice.service";
import { PlanTierService } from "../../plan-tier/plan-tier.service";
import { RememberService } from "../../remember/remember.service";
import { RepositoryUrlService } from "../../repository-url/repository-url.service";

@Component({
  selector: "ab-notice-detail",
  templateUrl: "./notice-detail.component.html",
})
export class NoticeDetailComponent implements OnInit {
  @Input() public project: any;
  @Input() public group: any;
  @Input() public noticeCount: number;
  @Input() public occurrence: any;
  @Input() public activeFilters: any;
  @Input() public filters: any;
  @Output() public toggleOccurrenceList = new EventEmitter();

  public loading: boolean;
  public prev: any;
  public hasPrevOccurrence: boolean;
  public next: any;
  public hasNextOccurrence: boolean;
  public isOpen: any;
  public filtering: boolean;
  public tierFeatures: any;
  public repoUrl: string;
  public deployRevision: string;
  public openPanelIds: string;

  private REMEMBER_KEY = "noticeDetail.isOpen";
  private DEFAULT_ACCORDION_OPEN_STATE = {
    details: true,
    backtrace: true,
    parameters: false,
    environment: false,
    session: false,
    contextHistory: false,
    context: false,
  };

  constructor(
    private $state: StateService,
    private deployService: DeployService,
    private noticeService: NoticeService,
    private planTierService: PlanTierService,
    private rememberService: RememberService,
    private repositoryUrlService: RepositoryUrlService,
  ) {}

  public loadOccurrence(pageName: string) {
    this.loading = true;

    switch (pageName) {
      case "prev":
      case "next":
        return this.$state.go("notices-show", {
          noticeId: (this as any)[pageName].id,
          tab: "notice-detail",
        });
      case "first":
      case "last":
        const params: any = {
          projectId: this.occurrence.projectId,
          groupId: this.occurrence.groupId,
        };
        merge(params, this.filters);

        return this.noticeService[pageName](params)
          .pipe(
            finalize(() => {
              this.loading = false;
            }),
          )
          .subscribe((occurrence: any) => {
            return this.$state.go("notices-show", {
              noticeId: occurrence.id,
              tab: "notice-detail",
            });
          });
      default:
        throw new Error(`unknown pageName: ${pageName}`);
    }
  }

  public hasOneOccurrence() {
    return this.noticeCount === 1;
  }

  public toggleOccurrences() {
    this.toggleOccurrenceList.emit();
  }

  public isEmpty(obj: any) {
    if (obj == null) {
      return true;
    }
    return isEqual({}, obj);
  }

  public ngOnInit() {
    this.setOccurrence(this.occurrence);

    const params: any = {
      projectId: this.occurrence.projectId,
      groupId: this.occurrence.groupId,
      notice_id: this.occurrence.id,
    };
    merge(params, this.filters);

    this.noticeService.prev(params).subscribe(
      (notice: any) => {
        this.prev = notice;
        this.hasPrevOccurrence = true;
      },
      (_error: HttpErrorResponse) => {
        this.hasPrevOccurrence = false;
      },
    );
    this.noticeService.next(params).subscribe(
      (notice: any) => {
        this.next = notice;
        this.hasNextOccurrence = true;
      },
      (_error: HttpErrorResponse) => {
        this.hasNextOccurrence = false;
      },
    );

    this.isOpen = Object.assign(
      cloneDeep(this.DEFAULT_ACCORDION_OPEN_STATE),
      JSON.parse(
        (this.rememberService.get(this.REMEMBER_KEY) as string) || "{}",
      ),
    );

    this.openPanelIds = Object.keys(this.isOpen)
      .filter((k: string) => {
        return this.isOpen[k];
      })
      .join(",");

    if (this.noticeCount < this.group.noticeCount && this.activeFilters) {
      this.filtering = true;
    }

    this.planTierService
      .features()
      .subscribe((features: any) => (this.tierFeatures = features));
  }

  public panelChange(event: any) {
    this.isOpen[event.panelId] = event.nextState;
    this.rememberService.set(this.REMEMBER_KEY, JSON.stringify(this.isOpen));
  }

  public backtrace() {
    if (!Array.isArray(this.occurrence.errors)) {
      return;
    }
    if (!Array.isArray(this.occurrence.errors[0].backtrace)) {
      return;
    }

    return this.occurrence.errors[0].backtrace;
  }

  private setOccurrence(notice: any) {
    this.occurrence = notice;
    this.repoUrl = this.repositoryUrlService.findForProject(this.project);

    if (notice.deployId > 0) {
      return this.deployService
        .get({ projectId: notice.projectId, deployId: notice.deployId })
        .subscribe((deploy: any) => (this.deployRevision = deploy.revision));
    }
  }
}
